<template>
	<div>
		<main class="container-xl py-3">

			<div v-if="loading" class="text-center py-5">
				<div class="spinner-border" role="status"></div>
			</div>

			<template v-else-if="proposal">

				<!-- Header card with quoted vs engine -->
				<div class="rounded bg-white mb-3" style="border:1px solid #e2e8f0;">
					<div class="px-3 py-2 d-flex align-items-baseline justify-content-between gap-2"
						style="background:#f8fafc;border-bottom:1px solid #e2e8f0;">
						<div>
							<div class="fw-semibold text-dark" style="font-size:14px;">
								{{ proposal.municipality_name_raw }}, {{ proposal.state }}
								<span class="ms-2 rounded-pill fw-medium px-2 py-0" style="font-size:10px;border:1px solid;"
									:style="outcomeStyle(proposal.outcome)">{{ proposal.outcome }}</span>
								<span class="ms-2 rounded fw-medium px-2 py-0" style="font-size:10px;"
									:style="proposal.customer_status === 'upgrade'
										? { background:'#e0f2fe', color:'#075985' }
										: { background:'#f1f5f9', color:'#334155' }">{{ proposal.customer_status }}</span>
							</div>
							<div style="font-size:11px;color:#64748b;">
								<span v-if="proposal.signed_date">Signed {{ fmtDate(proposal.signed_date) }} · </span>
								<span v-if="proposal.contract_years">{{ proposal.contract_years }}-yr term · </span>
								<span v-if="proposal.population">pop {{ Number(proposal.population).toLocaleString() }}</span>
								<span v-if="proposal.municipality && proposal.municipality.county" class="ms-1">· {{ proposal.municipality.county }}</span>
								<span v-if="!proposal.municipality_id" class="ms-1" style="color:#92400e;">⚠ unmatched</span>
							</div>
						</div>
						<div class="d-flex gap-2">
							<button type="button" @click="openRelink"
								class="btn btn-sm" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:4px 12px;">
								{{ proposal.municipality_id ? 'Re-link muni' : 'Link muni' }}
							</button>
							<button type="button" @click="loadComparison" :disabled="compLoading"
								class="btn btn-sm" style="border:1px solid #1d4ed8;background:white;color:#1d4ed8;font-size:12px;padding:4px 12px;">
								<span v-if="compLoading" class="spinner-border spinner-border-sm me-1"></span>
								{{ comparison ? 'Refresh comparison' : 'Run comparison' }}
							</button>
						</div>
					</div>

					<!-- Quoted vs engine totals (when comparison is loaded) -->
					<div v-if="comparison" class="row g-0">
						<div class="col-6" style="border-right:1px solid #e2e8f0;">
							<div class="px-3 py-2 text-uppercase fw-semibold" style="font-size:10px;letter-spacing:0.05em;color:#64748b;background:#fafbfc;border-bottom:1px solid #e2e8f0;">Quoted</div>
							<div class="row g-0 text-center">
								<div class="col-4 p-3" style="border-right:1px solid #e2e8f0;">
									<div class="text-uppercase mb-1" style="font-size:10px;color:#64748b;">Setup</div>
									<div class="fw-semibold font-monospace" style="font-size:18px;color:#0f172a;">{{ fmtCents(comparison.proposalSetupCents) }}</div>
								</div>
								<div class="col-4 p-3" style="border-right:1px solid #e2e8f0;background:rgba(236,253,245,0.4);">
									<div class="text-uppercase mb-1" style="font-size:10px;color:#047857;">Annual</div>
									<div class="fw-semibold font-monospace" style="font-size:18px;color:#065f46;">{{ fmtCents(comparison.proposalAnnualCents) }}</div>
								</div>
								<div class="col-4 p-3">
									<div class="text-uppercase mb-1" style="font-size:10px;color:#64748b;">Year 1</div>
									<div class="fw-semibold font-monospace" style="font-size:18px;color:#0f172a;">{{ fmtCents(comparison.proposalFirstYearCents) }}</div>
								</div>
							</div>
						</div>
						<div class="col-6">
							<div class="px-3 py-2 text-uppercase fw-semibold" style="font-size:10px;letter-spacing:0.05em;color:#1d4ed8;background:rgba(219,234,254,0.4);border-bottom:1px solid #e2e8f0;">
								Engine recommendation today
							</div>
							<div v-if="recommendedBundle" class="p-3">
								<div class="d-flex align-items-baseline gap-2 mb-1">
									<span class="fw-semibold text-dark" style="font-size:14px;">{{ recommendedBundle.bundleName }}</span>
									<span v-if="recommendedBundle.recommended" class="rounded fw-bold text-uppercase px-2 py-0" style="font-size:9px;background:#dcfce7;color:#166534;">recommended</span>
								</div>
								<div class="d-flex flex-wrap gap-3 font-monospace" style="font-size:13px;color:#0f172a;">
									<span><span style="color:#94a3b8;">Setup:</span> {{ fmtCents(recommendedBundle.setupCents) }}</span>
									<span><span style="color:#94a3b8;">Annual:</span> {{ fmtCents(recommendedBundle.annualCents) }}</span>
									<span class="fw-semibold"><span style="color:#94a3b8;font-weight:400;">Year 1:</span> {{ fmtCents(recommendedBundle.firstYearCents) }}</span>
								</div>
								<div v-if="deltaFirstYear !== null" class="mt-2" style="font-size:12px;"
									:style="{ color: deltaFirstYear > 0 ? '#0369a1' : deltaFirstYear < 0 ? '#be123c' : '#64748b' }">
									Δ vs engine year-1:
									<span class="font-monospace fw-semibold">
										{{ deltaFirstYear > 0 ? '+' : '' }}{{ fmtCents(deltaFirstYear) }}
									</span>
									<span v-if="deltaPctFirstYear !== null" style="color:#94a3b8;font-size:11px;">
										({{ deltaPctFirstYear > 0 ? '+' : '' }}{{ deltaPctFirstYear.toFixed(0) }}%)
									</span>
								</div>
							</div>
							<div v-else class="p-3" style="font-size:12px;color:#64748b;">Click "Run comparison" to see what the engine recommends today.</div>
						</div>
					</div>

					<!-- Engine bundles full table -->
					<div v-if="comparison && comparison.engineResult && comparison.engineResult.bundles" class="px-3 pb-3">
						<div class="text-uppercase fw-semibold mb-2 mt-3" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">All bundles</div>
						<table class="table table-sm mb-0" style="font-size:12px;">
							<thead style="background:#f8fafc;">
								<tr style="color:#475569;">
									<th class="fw-medium px-2 py-1">Bundle</th>
									<th class="fw-medium px-2 py-1 text-end">Setup</th>
									<th class="fw-medium px-2 py-1 text-end" style="background:rgba(236,253,245,0.4);">Annual</th>
									<th class="fw-medium px-2 py-1 text-end">Year 1</th>
									<th class="fw-medium px-2 py-1 text-end">Adj %</th>
								</tr>
							</thead>
							<tbody>
								<tr v-for="b in comparison.engineResult.bundles" :key="b.bundleId"
									:style="b.recommended ? { background: 'rgba(220,252,231,0.4)' } : {}">
									<td class="px-2 py-1">
										<span class="fw-medium" style="color:#0f172a;">{{ b.bundleName }}</span>
										<span v-if="b.recommended" class="ms-1 rounded fw-bold text-uppercase px-1" style="font-size:9px;background:#dcfce7;color:#166534;">rec</span>
									</td>
									<td class="px-2 py-1 text-end font-monospace" style="color:#475569;">{{ fmtCents(b.setupCents) }}</td>
									<td class="px-2 py-1 text-end font-monospace fw-semibold" style="background:rgba(236,253,245,0.4);color:#065f46;">{{ fmtCents(b.annualCents) }}</td>
									<td class="px-2 py-1 text-end font-monospace fw-semibold" style="color:#0f172a;">{{ fmtCents(b.firstYearCents) }}</td>
									<td class="px-2 py-1 text-end font-monospace"
										:style="{ color: (b.adjustmentPct || 0) > 0 ? '#047857' : (b.adjustmentPct || 0) < 0 ? '#be123c' : '#64748b' }">
										{{ (b.adjustmentPct || 0) >= 0 ? '+' : '' }}{{ ((b.adjustmentPct || 0) * 100).toFixed(1) }}%
									</td>
								</tr>
							</tbody>
						</table>
					</div>

					<!-- Adjustment factors -->
					<div v-if="adjustmentFactors.length" class="px-3 pb-3">
						<div class="text-uppercase fw-semibold mb-2 mt-2" style="font-size:10px;letter-spacing:0.05em;color:#64748b;">
							Active adjustment factors
						</div>
						<ul class="list-unstyled mb-0">
							<li v-for="(f, i) in adjustmentFactors" :key="i"
								class="d-flex align-items-baseline gap-2 py-1" style="font-size:12px;">
								<span class="rounded fw-medium px-2 py-0" style="font-size:10px;"
									:style="f.deltaPct > 0
										? { background:'#dcfce7', color:'#166534' }
										: { background:'#ffe4e6', color:'#9f1239' }">
									{{ f.label }}
								</span>
								<span class="font-monospace" style="color:#0f172a;">
									{{ f.deltaPct > 0 ? '+' : '' }}{{ (f.deltaPct * 100).toFixed(1) }}%
								</span>
								<span style="color:#64748b;">{{ f.reason }}</span>
							</li>
						</ul>
					</div>
				</div>

				<!-- Edit form -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<div class="d-flex align-items-baseline justify-content-between mb-2">
						<h2 class="h6 fw-semibold mb-0 text-dark">Edit proposal</h2>
						<div class="d-flex gap-2">
							<button class="btn btn-sm btn-primary" @click="save" :disabled="saving">
								<span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
								Save
							</button>
							<button class="btn btn-sm" style="border:1px solid #fecdd3;background:white;color:#9f1239;font-size:12px;padding:4px 12px;"
								@click="deleteProposal">Delete</button>
						</div>
					</div>
					<div class="row g-2">
						<div class="col-md-6">
							<label class="form-label form-label-sm">Setup ($)</label>
							<input type="number" min="0" class="form-control form-control-sm font-monospace" v-model.number="form.setup_dollars" />
						</div>
						<div class="col-md-6">
							<label class="form-label form-label-sm">Annual ($)</label>
							<input type="number" min="0" class="form-control form-control-sm font-monospace" v-model.number="form.annual_dollars" />
						</div>
						<div class="col-md-3">
							<label class="form-label form-label-sm">Contract yrs</label>
							<input type="number" min="1" max="10" class="form-control form-control-sm" v-model.number="form.contract_years" />
						</div>
						<div class="col-md-3">
							<label class="form-label form-label-sm">Population</label>
							<input type="number" min="0" class="form-control form-control-sm" v-model.number="form.population" />
						</div>
						<div class="col-md-3">
							<label class="form-label form-label-sm">Customer status</label>
							<select class="form-select form-select-sm" v-model="form.customer_status">
								<option value="new">new</option>
								<option value="upgrade">upgrade</option>
							</select>
						</div>
						<div class="col-md-3">
							<label class="form-label form-label-sm">Outcome</label>
							<select class="form-select form-select-sm" v-model="form.outcome">
								<option value="won">won</option>
								<option value="lost">lost</option>
								<option value="pending">pending</option>
							</select>
						</div>
						<div class="col-md-6">
							<label class="form-label form-label-sm">Matched bundle</label>
							<select class="form-select form-select-sm" v-model="form.matched_bundle_id">
								<option value="">— auto / none</option>
								<option value="b1">B1 Essential</option>
								<option value="b2">B2 Standard</option>
								<option value="b3">B3 Professional</option>
								<option value="b4">B4 Advanced</option>
								<option value="b5">B5 Enterprise</option>
							</select>
						</div>
						<div class="col-md-6">
							<label class="form-label form-label-sm">Signed date</label>
							<input type="date" class="form-control form-control-sm" v-model="form.signed_date" />
						</div>
						<div class="col-12">
							<label class="form-label form-label-sm">Notes</label>
							<textarea class="form-control form-control-sm" rows="3" v-model="form.notes"
								placeholder="Why won/lost, decision drivers, follow-ups…"></textarea>
						</div>
					</div>
				</div>

				<!-- Files panel -->
				<div class="rounded bg-white mb-3" style="border:1px solid #e2e8f0;">
					<header class="px-3 py-2 d-flex align-items-baseline justify-content-between"
						style="background:#f8fafc;border-bottom:1px solid #e2e8f0;">
						<div>
							<h3 class="h6 fw-semibold mb-0 text-dark">Files</h3>
							<p class="mb-0 mt-1" style="font-size:11px;color:#64748b;">Original proposal documents — PDF, JPG, PNG.</p>
						</div>
						<label class="btn btn-sm mb-0" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:4px 12px;cursor:pointer;">
							Upload
							<input type="file" accept=".pdf,.jpg,.jpeg,.png,.gif,.webp" class="d-none" ref="fileInput" @change="uploadFile" />
						</label>
					</header>
					<div v-if="proposal.files && proposal.files.length">
						<table class="table table-sm mb-0" style="font-size:12px;">
							<tbody>
								<tr v-for="f in proposal.files" :key="f.id" style="border-top:1px solid #f1f5f9;">
									<td class="px-3 py-2 fw-medium" style="color:#0f172a;">{{ f.original_filename }}</td>
									<td class="px-2 py-2" style="color:#64748b;">{{ f.mime_type }}</td>
									<td class="px-2 py-2 text-end font-monospace" style="color:#64748b;">{{ fmtBytes(f.size_bytes) }}</td>
									<td class="px-2 py-2 text-end" style="white-space:nowrap;">
										<a :href="downloadUrl(f)" target="_blank" rel="noreferrer"
											class="btn btn-sm" style="border:1px solid #cbd5e1;background:white;color:#1d4ed8;font-size:11px;padding:2px 8px;">Download</a>
										<button class="btn btn-sm ms-1" style="border:1px solid #fecdd3;background:white;color:#9f1239;font-size:11px;padding:2px 8px;"
											@click="deleteFile(f)">Delete</button>
									</td>
								</tr>
							</tbody>
						</table>
					</div>
					<div v-else class="px-3 py-3" style="font-size:12px;color:#64748b;">No files uploaded yet.</div>
				</div>

				<!-- Parse with Claude -->
				<div class="rounded bg-white p-3 mb-3" style="border:1px solid #e2e8f0;">
					<div class="d-flex align-items-baseline justify-content-between mb-2">
						<div>
							<h3 class="h6 fw-semibold mb-0 text-dark">Parse with Claude</h3>
							<p class="mb-0 mt-1" style="font-size:11px;color:#64748b;">
								Upload a competitor proposal PDF or image to extract line items + totals via Anthropic Claude.
							</p>
						</div>
						<label class="btn btn-sm mb-0" style="border:1px solid #cbd5e1;background:white;color:#334155;font-size:12px;padding:4px 12px;cursor:pointer;">
							Select file
							<input type="file" accept=".pdf,.jpg,.jpeg,.png" class="d-none" ref="parseFile" @change="parseDocument" />
						</label>
					</div>
					<div v-if="parsing" class="text-center py-3">
						<div class="spinner-border spinner-border-sm" role="status"></div>
						<span class="ms-2" style="font-size:12px;color:#64748b;">Parsing with Claude…</span>
					</div>
					<div v-if="parseResult" class="mt-2">
						<div v-if="parseResult.verification && parseResult.verification.valid"
							class="rounded p-2 mb-2" style="background:rgba(220,252,231,0.5);border:1px solid #86efac;font-size:12px;color:#166534;">
							✓ Verification passed
						</div>
						<div v-else-if="parseResult.verification && !parseResult.verification.valid"
							class="rounded p-2 mb-2" style="background:rgba(254,243,199,0.5);border:1px solid #fcd34d;font-size:12px;color:#92400e;">
							⚠ {{ parseResult.verification.notes || 'Verification did not match.' }}
						</div>
						<pre class="rounded p-2 mb-0" style="background:#f8fafc;border:1px solid #e2e8f0;font-size:11px;max-height:240px;overflow-y:auto;color:#334155;">{{ JSON.stringify(parseResult.parsed, null, 2) }}</pre>
					</div>
				</div>

				<!-- Metadata footer -->
				<div class="rounded bg-white p-3" style="border:1px solid #e2e8f0;font-size:12px;color:#64748b;">
					<span>ID: <code>{{ proposal.id }}</code></span>
					<span class="ms-3">Source: {{ proposal.source || '—' }}</span>
					<span class="ms-3">Population: {{ proposal.population ? Number(proposal.population).toLocaleString() : '—' }}</span>
					<span class="ms-3">Created: {{ fmtFullDate(proposal.created_at) }}</span>
					<span v-if="proposal.updated_at && proposal.updated_at !== proposal.created_at" class="ms-3">
						Updated: {{ fmtFullDate(proposal.updated_at) }}
					</span>
				</div>
			</template>
		</main>

		<!-- Re-link municipality modal -->
		<div v-if="relinkOpen" class="modal fade show d-block" tabindex="-1" @click.self="closeRelink"
			style="background:rgba(15,23,42,0.5);">
			<div class="modal-dialog modal-lg modal-dialog-centered">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Link this proposal to a municipality</h5>
						<button type="button" class="btn-close" :disabled="relinking" @click="closeRelink"></button>
					</div>
					<div class="modal-body">
						<div class="d-flex gap-2 mb-2">
							<input v-model="relinkQuery" type="text" placeholder="Municipality name"
								@keydown.enter.prevent="searchRelink"
								class="form-control flex-grow-1" />
							<select v-model="relinkState" class="form-select" style="max-width:120px;">
								<option value="">any</option>
								<option v-for="s in US_STATES" :key="s" :value="s">{{ s }}</option>
							</select>
							<button type="button" @click="searchRelink" :disabled="relinkLoading"
								class="btn btn-primary" style="font-size:13px;padding:6px 16px;">
								<span v-if="relinkLoading" class="spinner-border spinner-border-sm me-1"></span>
								{{ relinkLoading ? 'Searching…' : 'Search' }}
							</button>
						</div>
						<p class="mb-2" style="font-size:11px;color:#64748b;">
							Searches mc2's municipalities table by name + state. Pick the correct row to link this proposal.
						</p>
						<div v-if="relinkError" class="rounded p-2 mb-2" style="background:#fff1f2;border:1px solid #fecdd3;font-size:12px;color:#9f1239;">{{ relinkError }}</div>
						<ul v-if="relinkResults.length" class="list-unstyled rounded mb-0" style="border:1px solid #e2e8f0;max-height:360px;overflow-y:auto;">
							<li v-for="r in relinkResults" :key="r.id"
								class="d-flex align-items-center gap-2 px-3 py-2"
								style="border-bottom:1px solid #f1f5f9;">
								<div class="flex-grow-1 min-w-0">
									<div class="fw-medium" style="color:#0f172a;">
										{{ r.full_name }}<span class="fw-normal" style="color:#94a3b8;">, {{ r.state }}</span>
									</div>
									<div style="font-size:11px;color:#64748b;">
										<span v-if="r.county">{{ r.county }} · </span>
										<span v-if="r.population">pop {{ Number(r.population).toLocaleString() }}</span>
										<span v-else style="color:#cbd5e1;">pop —</span>
									</div>
								</div>
								<button type="button" @click="pickRelink(r)" :disabled="relinking"
									class="btn btn-sm btn-success flex-shrink-0" style="font-size:12px;padding:4px 12px;">
									{{ relinking ? 'Linking…' : 'Use this' }}
								</button>
							</li>
						</ul>
						<div v-else-if="relinkSearched && !relinkLoading" class="text-center py-3" style="font-size:12px;color:#64748b;">
							No matches — try widening the search or dropping the state filter.
						</div>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapState } from 'vuex'
import restApi from '../../api/index.js'

const US_STATES = ['AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT','VA','WA','WV','WI','WY','DC']

export default {
	name: 'CwpeProposalDetail',
	computed: {
		...mapState(['apiUrl']),
		recommendedBundle() {
			if (!this.comparison?.engineResult?.bundles) return null
			const list = this.comparison.engineResult.bundles
			return list.find(b => b.recommended) || list[0] || null
		},
		deltaFirstYear() {
			if (!this.comparison || !this.recommendedBundle) return null
			return this.comparison.proposalFirstYearCents - this.recommendedBundle.firstYearCents
		},
		deltaPctFirstYear() {
			if (this.deltaFirstYear === null) return null
			const eng = this.recommendedBundle?.firstYearCents
			if (!eng) return null
			return (this.deltaFirstYear / eng) * 100
		},
		adjustmentFactors() {
			const list = this.comparison?.engineResult?.bundles
			if (!list || !list.length) return []
			return list[0].adjustmentFactors || []
		},
	},
	data() {
		return {
			US_STATES,
			proposal: null,
			loading: false,
			saving: false,
			compLoading: false,
			comparison: null,
			parsing: false,
			parseResult: null,
			// Re-link muni dialog
			relinkOpen: false,
			relinkQuery: '',
			relinkState: '',
			relinkLoading: false,
			relinking: false,
			relinkResults: [],
			relinkError: null,
			relinkSearched: false,
			form: {
				setup_dollars: 0,
				annual_dollars: 0,
				contract_years: 1,
				population: null,
				customer_status: 'new',
				outcome: 'pending',
				matched_bundle_id: '',
				signed_date: '',
				notes: '',
			},
		}
	},
	created() {
		this.load()
	},
	methods: {
		load() {
			this.loading = true
			restApi.get(`/cwpe/proposals/${this.$route.params.id}`).then(({ data }) => {
				this.proposal = data
				this.form = {
					setup_dollars: (data.setup_cents || 0) / 100,
					annual_dollars: (data.annual_cents || 0) / 100,
					contract_years: data.contract_years || 1,
					population: data.population,
					customer_status: data.customer_status,
					outcome: data.outcome,
					matched_bundle_id: data.matched_bundle_id || '',
					signed_date: data.signed_date || '',
					notes: data.notes || '',
				}
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Failed to load', text: err?.response?.data?.error || '' })
			}).finally(() => { this.loading = false })
		},
		save() {
			this.saving = true
			const payload = {
				setup_cents: Math.round((this.form.setup_dollars || 0) * 100),
				annual_cents: Math.round((this.form.annual_dollars || 0) * 100),
				contract_years: this.form.contract_years,
				population: this.form.population || null,
				customer_status: this.form.customer_status,
				outcome: this.form.outcome,
				matched_bundle_id: this.form.matched_bundle_id || null,
				signed_date: this.form.signed_date || null,
				notes: this.form.notes || null,
			}
			restApi.put(`/cwpe/proposals/${this.$route.params.id}`, payload).then(({ data }) => {
				this.proposal = { ...this.proposal, ...data }
				this.$swal.fire({ icon: 'success', title: 'Saved', timer: 1200, showConfirmButton: false })
				if (this.comparison) this.loadComparison()
			}).catch(err => {
				const errMsg = err?.response?.data?.error
				this.$swal.fire({
					icon: 'error',
					title: 'Save failed',
					text: typeof errMsg === 'string' ? errMsg : JSON.stringify(errMsg || {}),
				})
			}).finally(() => { this.saving = false })
		},
		loadComparison() {
			this.compLoading = true
			restApi.get(`/cwpe/proposals/${this.$route.params.id}/comparison`).then(({ data }) => {
				this.comparison = data
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Comparison failed', text: err?.response?.data?.error || '' })
			}).finally(() => { this.compLoading = false })
		},
		uploadFile(e) {
			const file = e.target.files[0]
			if (!file) return
			const fd = new FormData()
			fd.append('file', file)
			restApi.post(`/cwpe/proposals/${this.$route.params.id}/files`, fd, { headers: { 'content-type': 'multipart/form-data' } })
				.then(() => this.load())
				.catch(err => {
					this.$swal.fire({ icon: 'error', title: 'Upload failed', text: err?.response?.data?.error || '' })
				})
				.finally(() => { if (this.$refs.fileInput) this.$refs.fileInput.value = '' })
		},
		deleteFile(file) {
			restApi.delete(`/cwpe/proposals/${this.$route.params.id}/files/${file.id}`).then(() => {
				this.load()
			}).catch(err => {
				this.$swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.error || '' })
			})
		},
		downloadUrl(file) {
			return `${this.apiUrl}/cwpe/proposals/${this.$route.params.id}/files/${file.id}`
		},
		parseDocument(e) {
			const file = e.target.files[0]
			if (!file) return
			const fd = new FormData()
			fd.append('file', file)
			this.parsing = true
			this.parseResult = null
			restApi.post('/cwpe/proposals/parse', fd, { headers: { 'content-type': 'multipart/form-data' } })
				.then(({ data }) => { this.parseResult = data })
				.catch(err => {
					this.$swal.fire({ icon: 'error', title: 'Parse failed', text: err?.response?.data?.error || '' })
				})
				.finally(() => { this.parsing = false; if (this.$refs.parseFile) this.$refs.parseFile.value = '' })
		},
		deleteProposal() {
			this.$swal.fire({
				title: 'Delete this proposal?',
				icon: 'warning',
				showCancelButton: true,
				confirmButtonText: 'Delete',
				confirmButtonColor: '#dc3545',
			}).then(result => {
				if (!result.isConfirmed) return
				restApi.delete(`/cwpe/proposals/${this.$route.params.id}`).then(() => {
					this.$router.push('/cwpe/proposals')
				}).catch(err => {
					this.$swal.fire({ icon: 'error', title: 'Delete failed', text: err?.response?.data?.error || '' })
				})
			})
		},
		// ── Re-link muni dialog ────────────────────────────────────────
		openRelink() {
			this.relinkQuery = this.proposal?.municipality_name_raw || ''
			this.relinkState = this.proposal?.state || ''
			this.relinkResults = []
			this.relinkError = null
			this.relinkSearched = false
			this.relinkOpen = true
			this.$nextTick(() => { if (this.relinkQuery) this.searchRelink() })
		},
		closeRelink() {
			if (this.relinking) return
			this.relinkOpen = false
		},
		searchRelink() {
			if (!this.relinkQuery || this.relinkQuery.trim().length < 2) return
			this.relinkLoading = true
			this.relinkError = null
			const search = this.relinkState
				? `${this.relinkQuery.trim()},${this.relinkState}`
				: this.relinkQuery.trim()
			restApi.get('/municipalities/search', { params: { search, limit: 15 } }).then(({ data }) => {
				const list = Array.isArray(data) ? data : (data?.results || data?.rows || [])
				this.relinkResults = list
				this.relinkSearched = true
			}).catch(err => {
				this.relinkError = err?.response?.data?.message || 'Search failed'
				this.relinkResults = []
			}).finally(() => { this.relinkLoading = false })
		},
		pickRelink(muni) {
			this.relinking = true
			this.relinkError = null
			restApi.patch(`/cwpe/proposals/${this.$route.params.id}/muni`, { municipality_id: muni.id }).then(() => {
				this.relinkOpen = false
				this.load()
				if (this.comparison) this.loadComparison()
				this.$swal.fire({ icon: 'success', title: 'Linked', timer: 1200, showConfirmButton: false })
			}).catch(err => {
				const errMsg = err?.response?.data?.error || err?.response?.data?.message
				this.relinkError = typeof errMsg === 'string' ? errMsg : (errMsg ? JSON.stringify(errMsg) : 'Link failed')
			}).finally(() => { this.relinking = false })
		},
		fmtCents(c) {
			if (c == null) return '—'
			return '$' + (c / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })
		},
		fmtBytes(n) {
			if (n == null) return '—'
			if (n < 1024) return `${n} B`
			if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`
			return `${(n / 1024 / 1024).toFixed(1)} MB`
		},
		fmtDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
		},
		fmtFullDate(val) {
			if (!val) return '—'
			return new Date(val).toLocaleString()
		},
		outcomeStyle(o) {
			if (o === 'won') return { background:'#d1fae5', color:'#065f46', borderColor:'#6ee7b7' }
			if (o === 'lost') return { background:'#ffe4e6', color:'#9f1239', borderColor:'#fda4af' }
			if (o === 'pending') return { background:'#fef3c7', color:'#92400e', borderColor:'#fcd34d' }
			return { background:'#f1f5f9', color:'#334155', borderColor:'#cbd5e1' }
		},
	},
}
</script>
